/* DOMNO. rewards — signed-out screen
   Rebuilds the sidebar's sign-in panel so the first thing someone sees is ours.
   Logged-out visitors only. The overlay is display:none until the person icon is clicked, so
   this swap happens long before anything is on screen — no flash.

   ===========================================================================
   8 Sep 2026 — the sign-in saga, so nobody repeats it.

   SYMPTOM 1: two buttons, 'Continue with email' and 'Continue with Shop', went to the same page.
   CAUSE: the Shop button was only real if an element #dp-shopbtn existed holding Shopify's
   {{ shop | login_button }}. That element is not rendered anywhere on this theme (verified live:
   getElementById returned null), so mountShop() always fell through to a fallback <a> pointing at
   the SAME email URL. Shopify's own hosted sign-in already shows a <shop-login> button under the
   email field, so ONE button gets the customer both routes. Fixed by dropping the fake second one.

   SYMPTOM 2: 'Something went wrong — redirect_uri mismatch' on Dom's phone.
   CAUSE: the URL was hand-built as
       /customer_authentication/login?return_to=%2F%3Fdp%3Dopen        (= return_to=/?dp=open)
   return_to only accepts a BARE RELATIVE PATH. Ours had a query string inside it, so Shopify
   rejected it. Proven by Dom on the same phone, same normal tab: the URL without return_to
   worked, the one with it did not. Shopify's docs say the same — "The return_to parameter only
   works with relative URLs."  https://shopify.dev/docs/storefronts/themes/sign-in

   SYMPTOM 3: removing return_to fixed the error but then dumped the customer on Shopify's own
   hosted account page after login, which is the opposite of what we want.

   THE ACTUAL ANSWER: stop hand-building the URL. Shopify generates the correct one as
   routes.storefront_login_url. It is a Liquid object, so
   snippets/domno-portal-loginurl.liquid publishes it as window.DP_LOGIN_URL from the layout.

   Getting the drawer back OPEN after login does not ride on the URL at all. Two mechanisms:
     1. the dp_reopen breadcrumb set below, and
     2. domno-portal.js checks document.referrer and reopens the drawer when the customer lands
        back from shopify.com / shop.app.

   ⚠ DO NOT hand-build this URL again, and never put a query string inside return_to.
   =========================================================================== */

/* ===========================================================================
   🔴 15 Sep 2026 — IT WENT TO A FULL DRAWER AND CAME STRAIGHT BACK. IT IS A DROPDOWN.

   Charlie asked for the drawer ("keeps it consistent"), saw it, and asked for the popout back
   the same afternoon: "put it back actually looked better as a popout." That returns it to
   Dom's own call of 10 Sep — "really like when you press the customer icon top right it has
   that drop down with the buttons as we can promote it really well like that" — so there is no
   longer anything to flag; the two of them agree.

   ⚠ WHAT CAME BACK IS THE SHAPE, NOT THE OLD SCREEN. The dropdown's original content said
   "a free piece, on us / one free just for signing up", "more ways to get another" and "account
   needed for your free piece". Every one of those lines is now false (there is no welcome
   piece) and Charlie killed them by name on 14 Sep. So the positioning machinery below is the
   proven code, lifted verbatim, and the panel inside it is the 15 Sep screen: the red line,
   "sign in to:", and the three tiles. Do not restore the old copy along with the old geometry.

   ⚠ AND THEN THE TILES CAME OUT AGAIN, same day. Charlie: "now remove the 3 widget sections,
   just put the text back as it was before." So the panel is back to a headline, ONE line of
   explanation and the two buttons — which is the 14 Sep shape he asked for on the screenshots:
   "just have one section that says something like 'Sign in to your DOMNO. account for order
   tracking, quick returns, and earn free items' centre aligned in the window."
   The icons and tile() went with them. If they are ever wanted back, they are in
   already-pushed/ under the 15 Sep dropdown build (sha16 3d4e55dd5c08da4c), not rewritten.

   ⚠ I COULD NOT RECOVER THE EXACT 14 SEP LINE. That file was overwritten in already-pushed/
   earlier the same day and Shopify keeps no theme file history, so the sentence below is written
   from Charlie's own instruction rather than restored. One word differs from what he typed:
   "free pieces", not "free items" — the house register is pieces everywhere else on the portal.
   =========================================================================== */

/* ===========================================================================
   THE DROPDOWN GEOMETRY, and why it is written this defensively.

   ⚠ THREE ATTEMPTS AT THIS, AND THE LESSON IS: DO NOT TRUST WHAT YOU MEASURE.
     1. Measured the person ICON's right edge — but the cart and burger sit to its right, so the
        panel stopped short of the edge. Dom: "weird in the middle".
     2. Used icon.closest('header') — and closest() matches the ELEMENT ITSELF, while the icon
        is a <button class="header-account-link">. It measured the 44px button. Same wrong answer.
     3. Walked up for the first ancestor 80% of viewport wide. Looked right, until it was checked
        on a live page and returned bottom:1373 — a full screen and a half DOWN the page, because
        `.header-account-link` does not always resolve to the thing in the header bar.

   So this no longer hunts for a header. It reads the icon, SANITY-CHECKS the answer (must be
   visible and within 220px of the top of the viewport), and ignores it otherwise, keeping the
   last good value rather than flinging the panel down the page. Horizontally it is simply flush
   to the right edge, which cannot be measured wrong.

   Dom, 11 Sep: "it should just follow the menu, right now it's stuck in the middle." DOMNO.'s
   header hides on scroll-down and returns on scroll-up, so this re-reads on every scroll frame
   and the panel tracks it. If the header genuinely leaves the screen, the panel closes with it —
   a menu floating over a page its trigger has left is the "stuck" state he is describing.
   =========================================================================== */
(function(){
  /* Shopify's own route. Fallback is the bare path, which Dom confirmed loads without error —
     it just would not return him to the store, hence the referrer check as backup. */
  function loginUrl(){
    return (window.DP_LOGIN_URL && String(window.DP_LOGIN_URL)) || '/customer_authentication/login';
  }

  function flag(){ try{ sessionStorage.setItem('dp_reopen','1'); }catch(e){} }

  /* ============ THE TEASE DOT IS GONE (14 Sep 2026) ============
     It used to live here: a red dot on the person icon for EVERY signed-out visitor, permanently,
     on the argument that "opening an account really does put a free piece in there". Two separate
     things killed it.

     1. THAT ARGUMENT STOPPED BEING TRUE. There is no welcome piece any more — the first ORDER
        earns one and the second spends it.

     2. IT HAD ALREADY BEEN INVISIBLE SINCE 12 SEP. domno-saved.js hides `.dp-dot` and replaced it
        with `.dp-badge` on the header star.

     The dot now belongs to domno-saved.js alone and it means one thing: a free piece is waiting.
     Do not add a second dot mechanism here; one icon, one owner. */

  function iconEl(){
    return document.querySelector('.header-account-link') ||
           document.querySelector('a[href*="/account"]');
  }

  /* the icon's onclick is inline in Charlie's header, so the class on the overlay is the only
     signal we reliably get that they actually opened it */
  function watchOpen(){
    var ov = document.getElementById('dp-overlay');
    if (!ov || ov.getAttribute('data-dp-teasewatch')) return;
    ov.setAttribute('data-dp-teasewatch', '1');
    var was = ov.classList.contains('open');
    if (was) unlock();
    try{
      new MutationObserver(function(){
        var now = ov.classList.contains('open');
        if (now && !was){ unlock(); anchor(); }
        was = now;
      }).observe(ov, { attributes:true, attributeFilter:['class'] });
    }catch(e){}
  }

  /* ⚠ A DROPDOWN MUST NOT LOCK THE PAGE. Dom, 11 Sep: "you can't scroll the website when it's
     up... it's stuck in the middle of the page, you have to cancel it."
     The drawer's openers all set documentElement.overflow = 'hidden', which is right for a
     full-height modal and wrong for this. Worse, the lock is not airtight on iOS, so the page
     crept anyway while the panel stayed at the top it was measured at — hence "stuck in the
     middle". Signed out we simply give the page back. The close handlers already reset this, so
     clearing it early costs nothing.
     ⚠ THIS IS THE LINE THAT HAD TO COME BACK ON 15 SEP. For the few hours the panel was a full
     drawer, removing it was correct — a drawer SHOULD lock. It is a dropdown again, so it must
     not. If the shape ever changes again, this function changes with it. */
  function unlock(){
    try{ document.documentElement.style.overflow = ''; }catch(e){}
  }

  /* ...and because the header moves, following it means re-measuring as the page moves.
     rAF-throttled so a fast scroll cannot queue hundreds of layout reads. */
  var rafId = 0;
  function onScroll(){
    if (rafId) return;
    rafId = requestAnimationFrame(function(){
      rafId = 0;
      var ov = document.getElementById('dp-overlay');
      if (ov && ov.classList.contains('open')) anchor();
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  var lastTop = 56;

  function headerOffset(){
    var icon = iconEl();
    if (icon){
      try{
        var r = icon.getBoundingClientRect();
        /* plausible only if it is actually up in the header band */
        if (r.height > 0 && r.bottom > 0 && r.bottom < 220){
          lastTop = Math.round(r.bottom + 6);
          return lastTop;
        }
        /* icon has scrolled away: the menu this belongs to is gone, so go with it */
        if (r.height > 0 && r.bottom <= 0) return -1;
      }catch(e){}
    }
    return lastTop;
  }

  /* data-dp-signedout is what switches the CSS in snippets/domno-portal-loginurl.liquid from
     drawer to dropdown. Without this stamp the panel is a full-height drawer again. */
  function anchor(){
    var ov = document.getElementById('dp-overlay');
    if (!ov) return;
    ov.setAttribute('data-dp-signedout', '1');
    var top = headerOffset();
    if (top < 0){
      ov.classList.remove('open');
      unlock();
      return;
    }
    ov.style.setProperty('--dp-anchor-top', top + 'px');
    ov.style.setProperty('--dp-anchor-right', '0px');
  }

  /* re-measure rather than leaving the panel floating where the icon used to be */
  window.addEventListener('resize', anchor);
  window.addEventListener('orientationchange', function(){ setTimeout(anchor, 120); });

  function build(panel){
    var url = loginUrl();

    /* 🔴 15 Sep 2026, final shape: the red line, ONE line saying what an account is for, and the
       two buttons. Charlie got tiles for an afternoon and took them back out.

       ⚠ NO WORDMARK IN HERE. It was added for the few hours this was a full drawer, where a
       blank top read as broken. A dropdown hangs off the header, which already has DOMNO. in it
       — a second one 40px below the first looked like a mistake.

       ⚠ THE SUB LINE CARRIES class="c" ON PURPOSE. That is the portal's own small-copy class and
       it is in the ALL CAPS list in snippets/domno-portal-loginurl.liquid, so this renders in
       caps like the rest of the drawer. Do not restyle it locally.

       ⚠ THE SUB LINE SAYS "FREE PIECES" AND NOT WHEN. The headline directly above already says
       every order earns one, so repeating the timing underneath read as a stutter. What this
       line must never do is imply one arrives for signing up, which is exactly what this panel
       promised until 14 Sep.

       ⚠ The two buttons point at the SAME place on purpose. On Shopify customer accounts, signing
       in and creating an account are one flow: give an email, get a six-digit code, and a new
       email makes the account on the spot. There is no password on this store for anyone. They
       are two buttons because a new customer will not press something called "sign in". */
    panel.innerHTML =
      '<button class="dp-x dp-x--signin" aria-label="Close" ' +
        'onclick="document.getElementById(\'dp-overlay\').classList.remove(\'open\');' +
        'document.documentElement.style.overflow=\'\'">&#10005;</button>' +

      '<div class="dp-si">' +

        '<div class="dp-si__head">every order earns a free piece</div>' +

        '<div class="c dp-si__sub">sign in to your DOMNO. account for order tracking, ' +
          'quick returns and free pieces.</div>' +

        '<a class="btn primary block" id="dp-continue" href="' + url + '">Sign in</a>' +
        '<a class="btn ghost block" id="dp-create" style="margin-top:9px" href="' + url + '">' +
          'Create an account</a>' +

      '</div>';

    var outs = panel.querySelectorAll('#dp-continue, #dp-create');
    for(var i=0;i<outs.length;i++){ outs[i].addEventListener('click', flag, true); }

    anchor();
    watchOpen();
  }

  var tries = 0;
  var timer = setInterval(function(){
    tries++;
    var panel = document.querySelector('#domno-portal .signin');
    if (panel) { clearInterval(timer); try { build(panel); } catch(e){} }
    else if (tries > 60) { clearInterval(timer); }
  }, 100);
})();